const integer = new Intl.NumberFormat("en-US");

export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : integer.format(value);

export const formatPercent = (part: number, whole: number): string =>
  whole === 0 ? "—" : `${Math.round((part / whole) * 100)}%`;

/** YYYY-MM-DD in local time — the format every API date param uses. */
export function isoDate(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return isoDate(date);
}

export const yesterday = (): string => daysAgo(1);

export function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/** Elapsed time between two timestamps, for the run history table. */
export function formatDuration(start: string | null, end: string | null): string {
  if (!start || !end) return "—";
  const seconds = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000),
  );
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** `day` (YYYY-MM-DD) moved by whole days, still local-time. */
export function shiftDate(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00`);
  date.setDate(date.getDate() + days);
  return isoDate(date);
}

/** Whole days from `from` to `to`, both YYYY-MM-DD. */
export const daysBetween = (from: string, to: string): number =>
  Math.round(
    (new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) /
      86_400_000,
  );

/** Every local day in an inclusive YYYY-MM-DD range. */
export function eachDay(from: string, to: string): string[] {
  const days: string[] = [];
  for (let day = from; day <= to; day = shiftDate(day, 1)) days.push(day);
  return days;
}

/** The currency of the sales exports and every money figure. */
export const CURRENCY = "EGP";

const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

/** An amount in Egyptian pounds (`CURRENCY`), the currency of every POS
 *  export. No symbol in the number itself: the card shows the unit beside
 *  it. Whole units; `short` compacts 1,234,567 to 1.2M. */
export function formatMoney(value: number | null | undefined, short = false): string {
  if (value === null || value === undefined) return "—";
  return short && Math.abs(value) >= 10_000 ? compact.format(value) : money.format(value);
}

/** A ratio as a percentage with one decimal, e.g. 0.1234 → "12.3%". */
export function formatRatio(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

/** "Aug 5" — a compact axis tick for a YYYY-MM-DD day. */
export function shortDay(day: string): string {
  const date = new Date(`${day}T00:00:00`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Tue, Aug 5" — a tooltip heading for a YYYY-MM-DD day. */
export function longDay(day: string): string {
  const date = new Date(`${day}T00:00:00`);
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** 0 = Sunday … 6 = Saturday, as `Date.getDay()`. */
export const weekdayOf = (day: string): number => new Date(`${day}T00:00:00`).getDay();

export const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** First and last day of the month `offset` months from the current one
 *  (0 = this month, -1 = last month), clipped to today. */
export function monthRange(offset: number): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const to = last > now ? now : last;
  return { from: isoDate(first), to: isoDate(to) };
}

const bytesFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

/** 1536 → "1.5 KB", for download sizes. */
export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${bytesFormat.format(size)} ${units[unit]}`;
}
